import {
  AnniversaryIntervalEnum,
  FrequencyEnum,
  IntervalEnum,
  NullEnum,
  PayoutTypeEnum,
  SalaryTypeEnum,
  SpecialOccasionTypeEnum,
  TaxTreatmentEnum
} from '@pxtra-client-lib-legacy/gen';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useCreateBonus } from '../hooks/useCreateBonus';
import { useStepper } from '../../partials/actionStepperPartial';
import { useEffect, useMemo, useState } from 'react';
import { ArrowForwardIconSvg } from '../../../../lib/icons';
import { FeedbackAlert } from '../../../../lib/components/atomic/feedback';
import { SelectionCard } from '../../../../lib/components/atomic/dataDisplay/selectionCard';
import { Drawer } from '../../../../lib/components/atomic/dataDisplay/drawer';
import { InfoSection } from '../../../../lib/components/atomic/typography/InfoSection';
import { Select, TextField, DatePicker } from '../../../../lib/components';
import { InputFieldLabel } from '../../../../lib/components/atomic/input/shared/fieldLabel';
import { SALARY_TYPES, PARTIAL_SPECIAL_OCCASION_TYPES, PAYOUT_TYPES } from '../store';
import { useFormik, FormikProvider, FormikValues } from 'formik';
import { payoutTypeSchema } from '../utils/validationSchemas';
import { ConfirmationDialog } from '../../../../lib/components/compound/dialogs/confirmationDialog';
import { CurrencyFrequencyField } from '../components/CurrencyFrequencyField';
import { MonthRange } from '../components/MonthRange';
import { PayoutMonth } from '../components/PayoutMonth';

interface PayoutStepValues extends FormikValues {
  taxTreatment: TaxTreatmentEnum | undefined;
  specialOccasionType: SpecialOccasionTypeEnum | NullEnum | undefined;
  payoutType: PayoutTypeEnum | undefined;
  amount: number | undefined;
  frequency: FrequencyEnum | NullEnum | undefined | null;
  eventMonth: Date | null | undefined;
  payoutMonth: Date | null | undefined;
  startAccountingPeriod: Date | null | undefined;
  endAccountingPeriod: Date | null | undefined;
  interval: IntervalEnum[] | null | undefined;
  anniversaryInterval: AnniversaryIntervalEnum | NullEnum | undefined;
  salaryType: SalaryTypeEnum | NullEnum | undefined;
  salaryTypeId: string | undefined;
}

export const PayoutType = observer(() => {
  const { t } = useTranslation();
  const { newBonus } = useCreateBonus();
  const {
    activeStep,
    showInfoDrawer,
    editMode,
    setActiveStep,
    setShowInfoDrawer,
    setNextButtonConfig
  } = useStepper();

  const formik = useFormik<PayoutStepValues>({
    initialValues: {
      taxTreatment: newBonus.taxTreatment,
      specialOccasionType: newBonus.specialOccasionType,
      payoutType: newBonus.payoutType,
      amount: newBonus.amount ? newBonus.amount / 100 : undefined,
      frequency: newBonus.frequency,
      eventMonth: newBonus.eventMonth,
      payoutMonth: newBonus.payoutMonth,
      startAccountingPeriod: newBonus.startAccountingPeriod,
      endAccountingPeriod: newBonus.endAccountingPeriod,
      interval: newBonus.interval,
      anniversaryInterval: newBonus.anniversaryInterval,
      salaryType: newBonus.salaryType,
      salaryTypeId: newBonus.salaryTypeId
    },
    enableReinitialize: true,
    validationSchema: payoutTypeSchema(t, newBonus.editingNotAllowed),
    onSubmit: (values) => {
      newBonus.payoutType = values.payoutType as PayoutTypeEnum;
      newBonus.amount = values.amount ? values.amount * 100 : undefined;
      newBonus.frequency = values.frequency;
      newBonus.eventMonth = values.eventMonth ?? undefined;
      newBonus.payoutMonth = values.payoutMonth ?? undefined;
      newBonus.startAccountingPeriod = values.startAccountingPeriod ?? undefined;
      newBonus.endAccountingPeriod = values.endAccountingPeriod ?? undefined;
      newBonus.interval = values.interval as IntervalEnum[];
      newBonus.anniversaryInterval = values.anniversaryInterval as AnniversaryIntervalEnum;
      newBonus.salaryType = values.salaryType as SalaryTypeEnum;
      newBonus.salaryTypeId = values.salaryTypeId;

      if (editMode) {
        setActiveStep(6);
      } else {
        setActiveStep(activeStep + 1);
      }
    }
  });

  const { values, errors, touched, setFieldValue, setValues, setTouched, submitForm } = formik;

  const [showFewDaysDialog, setShowFewDaysDialog] = useState(false);

  const daysLeftInMonth = useMemo(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate() - today.getDate() - 1;
  }, []);

  const shouldWarnFewDays =
    values.taxTreatment === 'SPECIAL_OCCASION' &&
    values.payoutType === 'APP_VOUCHER' &&
    PARTIAL_SPECIAL_OCCASION_TYPES.includes(
      values.specialOccasionType as SpecialOccasionTypeEnum
    ) &&
    !!values.payoutMonth &&
    new Date(values.payoutMonth).getFullYear() === new Date().getFullYear() &&
    new Date(values.payoutMonth).getMonth() === new Date().getMonth() &&
    daysLeftInMonth <= 5;

  const payoutMonthDisabled =
    newBonus.editingNotAllowed ||
    (values.taxTreatment !== 'SPECIAL_OCCASION' && values.frequency === 'RECURRING');

  const currentPayoutTypes =
    newBonus.taxTreatment === 'FULLY_TAXED' || newBonus.taxTreatment === 'RELIEF_PREMIUM_2026'
      ? (['SALARY'] as const)
      : PAYOUT_TYPES.filter((type) => type !== 'SALARY');

  const payoutMonthMaxDate =
    values.taxTreatment === 'RELIEF_PREMIUM_2026' ? new Date(2027, 5, 30) : undefined;

  const payoutMonthMinDate =
    values.taxTreatment === 'RELIEF_PREMIUM_2026' ? new Date(2026, 0, 1) : undefined;

  const anniversaryIntervalOptions = [1, 5, 10, 15, 20].map((item) => ({
    value: item,
    text:
      values.frequency === 'ONE_TIME'
        ? item === 1
          ? t('bonus:firstAnniversary')
          : t('bonus:anniversaryIntervalOneTime', { count: item })
        : item === 1
          ? t('bonus:yearly')
          : t('bonus:anniversaryIntervalRecurring', { count: item })
  }));

  const salaryTypeOptions = SALARY_TYPES.map((type) => ({ value: type, text: t(`bonus:${type}`) }));

  const updatePayoutType = (payoutType: PayoutTypeEnum) => {
    setTouched(
      {
        ...touched,
        payoutType: true,
        amount: false,
        frequency: false,
        startAccountingPeriod: false,
        endAccountingPeriod: false,
        anniversaryInterval: false,
        interval: false
      },
      true
    );
    setValues({ ...values, payoutType: payoutType });
  };

  const handleStartDateChange = (date: string) => {
    setFieldValue('startAccountingPeriod', new Date(date));
    if (values.specialOccasionType === 'BIRTHDAY' && values.frequency === 'ONE_TIME' && editMode) {
      const endDate = new Date(date);
      endDate.setFullYear(endDate.getFullYear() + 1);
      setFieldValue('endAccountingPeriod', endDate);
    }
  };

  const handleEndDateChange = (date: string | null) => {
    const value = date ? new Date(date) : undefined;
    setFieldValue('endAccountingPeriod', value);
  };

  const previousMonthStart = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);

  const monthStart = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
  const nextMonthStart = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 1);

  const derivePayoutMonth = (eventMonth: Date) => {
    const today = new Date();
    const current = new Date(today.getFullYear(), today.getMonth(), 1);
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const isLastDay = today.getDate() === lastDayOfMonth;
    const event = monthStart(eventMonth);

    if (event < current) {
      const value = isLastDay ? nextMonth : current;
      return { min: value, max: value, default: value };
    }
    if (event.getTime() === current.getTime()) {
      if (isLastDay) {
        return { min: nextMonth, max: nextMonth, default: nextMonth };
      }
      return { min: current, max: nextMonth, default: current };
    }
    return { min: event, max: nextMonthStart(event), default: event };
  };

  const handleEventMonthChange = (date: string) => {
    const newEvent = new Date(date);
    setFieldValue('eventMonth', newEvent);
    setFieldValue('payoutMonth', derivePayoutMonth(newEvent).default);
  };

  const getPayoutMonthMin = () =>
    values.eventMonth ? derivePayoutMonth(values.eventMonth).min : undefined;

  const payoutMonthMax = values.eventMonth ? derivePayoutMonth(values.eventMonth).max : undefined;

  const nextButtonConfig = useMemo(
    () => ({
      text: editMode ? t('common:CONFIRM') : t('common:NEXT'),
      endIcon: <ArrowForwardIconSvg />,
      onClick: () => {
        if (shouldWarnFewDays) {
          setShowFewDaysDialog(true);
        } else {
          submitForm();
        }
      }
    }),
    [editMode, t, submitForm, shouldWarnFewDays, setShowFewDaysDialog]
  );

  useEffect(() => {
    setNextButtonConfig(nextButtonConfig);
  }, [nextButtonConfig, setNextButtonConfig]);

  useEffect(() => {
    if (
      (values.taxTreatment === 'FULLY_TAXED' || values.taxTreatment === 'RELIEF_PREMIUM_2026') &&
      values.payoutType !== 'SALARY'
    ) {
      setFieldValue('payoutType', 'SALARY');
    }
  }, [values.taxTreatment, values.payoutType, setFieldValue]);

  return (
    <FormikProvider value={formik}>
      <div className="flex flex-col">
        <div className="-mt-2 mb-2 flex flex-col gap-y-8">
          {errors.payoutType && touched.payoutType && (
            <FeedbackAlert description={t('bonus:error:payoutTypeMissing')} severity="error" />
          )}
          <div>{t('bonus:subtitle:payoutType')}</div>
        </div>
        <div className="flex flex-col gap-y-6">
          <div className="flex flex-col gap-y-4 mt-6">
            {currentPayoutTypes.map((payoutType) => (
              <div key={payoutType}>
                <SelectionCard
                  key={payoutType}
                  item={payoutType}
                  isSelected={values.payoutType === payoutType}
                  onSelect={(payoutType) => updatePayoutType(payoutType as PayoutTypeEnum)}
                />
              </div>
            ))}
          </div>
          {values.payoutType && (
            <>
              {/* SPECIAL OCCASION */}
              {values.taxTreatment === 'SPECIAL_OCCASION' && (
                <>
                  {/* BIRTHDAY */}
                  {values.specialOccasionType === 'BIRTHDAY' && (
                    <div className="flex flex-col gap-y-6">
                      <div className="w-full flex gap-x-4">
                        <div className="w-1/3">
                          <CurrencyFrequencyField />
                        </div>
                        <div className="w-2/3">
                          <MonthRange
                            hideInterval
                            handleStartDateChange={handleStartDateChange}
                            handleEndDateChange={handleEndDateChange}
                          />
                        </div>
                      </div>
                      {values.frequency && (
                        <div className="flex flex-col gap-y-4">
                          <FeedbackAlert
                            description={t(`bonus:alerts:${values.frequency}`)}
                            severity="info"
                          />
                          {values.frequency === 'ONE_TIME' && (
                            <FeedbackAlert
                              description={t('bonus:alerts:birthdayOneTime')}
                              severity="warning"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {/* ANNIVERSARY */}
                  {values.specialOccasionType === 'ANNIVERSARY' && (
                    <div className="flex flex-col gap-y-6">
                      <CurrencyFrequencyField />
                      <div className="flex gap-x-4">
                        <div className="w-1/3">
                          <Select
                            label={
                              values.frequency === 'ONE_TIME'
                                ? t('bonus:setAnniversary')
                                : t('bonus:setAnniversaryInterval')
                            }
                            value={values.anniversaryInterval}
                            options={anniversaryIntervalOptions}
                            placeholder={
                              values.frequency === 'ONE_TIME'
                                ? t('bonus:anniversaryIntervalPlaceholderOneTime')
                                : t('bonus:anniversaryIntervalPlaceholderRecurring')
                            }
                            required
                            error={touched.anniversaryInterval && !!errors.anniversaryInterval}
                            helperText={
                              (touched.anniversaryInterval && errors.anniversaryInterval) as string
                            }
                            onChange={(e) => {
                              setFieldValue('anniversaryInterval', Number(e.target.value));
                            }}
                          />
                        </div>
                        <div className="w-2/3">
                          <MonthRange
                            hideInterval
                            handleStartDateChange={handleStartDateChange}
                            handleEndDateChange={handleEndDateChange}
                          />
                        </div>
                      </div>
                      <FeedbackAlert description={t('bonus:alerts:anniversary')} severity="info" />
                    </div>
                  )}
                  {/* OTHER */}
                  {PARTIAL_SPECIAL_OCCASION_TYPES.includes(
                    values.specialOccasionType as SpecialOccasionTypeEnum
                  ) && (
                    <div className="flex flex-col gap-y-6">
                      <FeedbackAlert
                        description={
                          <div className="flex flex-col">
                            <span>
                              <strong>{t('bonus:alerts:specialOccasionTaxHintTitle')}</strong>{' '}
                              {t('bonus:alerts:specialOccasionTaxHintDescription')}
                            </span>
                          </div>
                        }
                        severity="error"
                      />
                      <div className="flex gap-x-4">
                        <div className="w-1/3">
                          <CurrencyFrequencyField hideFrequency />
                        </div>
                        <div className="w-1/3">
                          <InputFieldLabel
                            label={t('bonus:eventMonth')}
                            required
                            tooltip={t('bonus:tooltip:eventMonth')}
                          />
                          <DatePicker
                            variant="month"
                            placeholder={t('bonus:selectEventMonth')}
                            initialValue={values.eventMonth?.toISOString()}
                            minDate={previousMonthStart}
                            required
                            onChange={handleEventMonthChange}
                            onClear={() => {
                              setFieldValue('eventMonth', undefined);
                              setFieldValue('payoutMonth', undefined);
                            }}
                          />
                        </div>
                        <div className="w-1/3">
                          <InputFieldLabel
                            label={t('bonus:payoutMonth')}
                            required
                            disabled={!values.eventMonth}
                          />
                          <DatePicker
                            variant="month"
                            initialValue={values.payoutMonth?.toString()}
                            placeholder={t('bonus:selectPayoutMonth')}
                            required
                            disabled={!values.eventMonth}
                            minDate={getPayoutMonthMin()}
                            maxDate={payoutMonthMax}
                            error={touched.payoutMonth && !!errors.payoutMonth}
                            helperText={(touched.payoutMonth && errors.payoutMonth) as string}
                            onChange={(date) => {
                              setFieldValue('payoutMonth', new Date(date));
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              {/* NON_CASH_GIFT, NON_CASH_BENEFIT, FULLY_TAXED, RELIEF_PREMIUM_2026  */}
              {['NON_CASH_GIFT', 'NON_CASH_BENEFIT', 'FULLY_TAXED', 'RELIEF_PREMIUM_2026'].includes(
                values.taxTreatment as TaxTreatmentEnum
              ) && (
                <div className="flex flex-col gap-y-6">
                  <div className="flex gap-x-4">
                    <CurrencyFrequencyField />
                    <PayoutMonth
                      disabled={payoutMonthDisabled}
                      maxDate={payoutMonthMaxDate}
                      minDate={payoutMonthMinDate}
                    />
                  </div>
                  {values.taxTreatment === 'FULLY_TAXED' && (
                    <div className="flex gap-x-4">
                      <Select
                        label={t('bonus:salaryType')}
                        value={values.salaryType}
                        options={salaryTypeOptions}
                        placeholder={t('bonus:salaryTypePlaceholder')}
                        required
                        error={touched.salaryType && !!errors.salaryType}
                        helperText={(touched.salaryType && errors.salaryType) as string}
                        onChange={(e) => {
                          setFieldValue('salaryType', e.target.value);
                        }}
                      />
                      <TextField
                        label={t('bonus:salaryTypeId')}
                        value={values.salaryTypeId}
                        placeholder={t('bonus:salaryTypeIdPlaceholder')}
                        required
                        error={touched.salaryTypeId && !!errors.salaryTypeId}
                        helperText={(touched.salaryTypeId && errors.salaryTypeId) as string}
                        onChange={(e) => {
                          setFieldValue('salaryTypeId', e.target.value);
                        }}
                      />
                    </div>
                  )}
                  {values.frequency === 'RECURRING' && (
                    <MonthRange
                      handleStartDateChange={handleStartDateChange}
                      handleEndDateChange={handleEndDateChange}
                      maxDate={payoutMonthMaxDate}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <ConfirmationDialog
        open={showFewDaysDialog}
        variant="warning"
        width="480px"
        title={t('bonus:alerts:fewDaysLeftTitle', { count: daysLeftInMonth })}
        contentText={t('bonus:alerts:fewDaysLeftContent', { count: daysLeftInMonth })}
        confirmButtonText={t('common:NEXT')}
        cancelButtonText={t('common:CANCEL')}
        onConfirm={() => {
          setShowFewDaysDialog(false);
          submitForm();
        }}
        onCancel={() => setShowFewDaysDialog(false)}
        onClose={() => setShowFewDaysDialog(false)}
      />
      <Drawer
        label={t('bonus:payoutTypeDrawer:title')}
        open={showInfoDrawer}
        closeDrawer={() => setShowInfoDrawer(false)}
      >
        <div className="flex flex-col gap-y-6">
          <InfoSection content={t('bonus:payoutTypeDrawer:texts', { returnObjects: true })} />
        </div>
      </Drawer>
    </FormikProvider>
  );
});
